import React, { useState, useEffect } from 'react';
import { useProducts } from '../context/ProductsContext';

const ProductManagementInterface = ({ isOpen, onClose }) => {
  const { products } = useProducts();
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showUnitManager, setShowUnitManager] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // États pour le formulaire de produit
  const [productForm, setProductForm] = useState({
    name: '',
    category: 'Bœuf',
    price: '',
    unit: 'kg',
    stock: '',
    image: '',
    description: '',
    featured: false
  });

  // État pour l'aperçu de l'image
  const [imagePreview, setImagePreview] = useState('');

  // États pour les catégories et unités
  const [categories, setCategories] = useState(['Bœuf', 'Porc', 'Agneau', 'Volaille', 'Charcuterie', 'Œufs']);
  const [units, setUnits] = useState(['kg', 'Pièces', 'Barquettes', 'Douzaine']);

  // Charger les catégories et unités depuis localStorage
  useEffect(() => {
    const savedCategories = localStorage.getItem('productCategories');
    const savedUnits = localStorage.getItem('productUnits');
    
    if (savedCategories) {
      setCategories(JSON.parse(savedCategories));
    }
    if (savedUnits) {
      setUnits(JSON.parse(savedUnits));
    }
  }, []);

  // Sauvegarder les catégories et unités
  const saveCategories = (newCategories) => {
    setCategories(newCategories);
    localStorage.setItem('productCategories', JSON.stringify(newCategories));
    
    // Déclencher la synchronisation des catégories
    window.dispatchEvent(new CustomEvent('categoriesUpdated', {
      detail: { categories: newCategories }
    }));
    
    // Déclencher un événement de storage pour les autres composants
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'productCategories',
      newValue: JSON.stringify(newCategories)
    }));
    
    console.log('📂 Catégories mises à jour:', newCategories);
  };

  const saveUnits = (newUnits) => {
    setUnits(newUnits);
    localStorage.setItem('productUnits', JSON.stringify(newUnits));
    
    // Déclencher la synchronisation des unités
    window.dispatchEvent(new CustomEvent('unitsUpdated', {
      detail: { units: newUnits }
    }));
    
    console.log('📏 Unités mises à jour:', newUnits);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Mettre à jour l'aperçu de l'image si c'est le champ image
    if (name === 'image' && value) {
      setImagePreview(value);
    }
  };

  // Gestion de l'upload de fichier image
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est bien une image
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageDataUrl = event.target.result;
          setProductForm(prev => ({
            ...prev,
            image: imageDataUrl
          }));
          setImagePreview(imageDataUrl);
        };
        reader.readAsDataURL(file);
      } else {
        alert('Veuillez sélectionner un fichier image valide (JPG, PNG, GIF, etc.)');
      }
    }
  };

  // Créer ou modifier un produit
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const savedProducts = localStorage.getItem('farmProducts');
    const products = savedProducts ? JSON.parse(savedProducts) : [];
    const nextId = Math.max(...products.map(p => p.id), 0) + 1;
    
    const productData = {
      id: editingProduct ? editingProduct.id : nextId,
      name: productForm.name,
      category: productForm.category,
      price: parseFloat(productForm.price),
      unit: productForm.unit,
      stock: parseInt(productForm.stock),
      image: productForm.image || '/images/beef-steak.svg',
      description: productForm.description,
      featured: productForm.featured,
      createdAt: editingProduct ? editingProduct.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (editingProduct) {
      // Modifier le produit existant
      const index = products.findIndex(p => p.id === editingProduct.id);
      if (index !== -1) {
        products[index] = productData;
      }
    } else {
      // Ajouter un nouveau produit
      products.push(productData);
    }

    // Sauvegarder et synchroniser
    localStorage.setItem('farmProducts', JSON.stringify(products));
    window.dispatchEvent(new CustomEvent('productsUpdated', {
      detail: { products, count: products.length }
    }));

    console.log(editingProduct ? '✅ Produit modifié:' : '✅ Produit créé:', productData.name);
    
    // Réinitialiser le formulaire
    resetForm();
    onClose();
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setProductForm({
      name: '',
      category: categories[0] || 'Bœuf',
      price: '',
      unit: units[0] || 'kg',
      stock: '',
      image: '',
      description: '',
      featured: false
    });
    setImagePreview('');
    setEditingProduct(null);
  };

  // Modifier un produit existant
  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      unit: product.unit,
      stock: product.stock.toString(),
      image: product.image,
      description: product.description,
      featured: product.featured
    });
    setImagePreview(product.image);
  };

  // Supprimer un produit
  const deleteProduct = (productId) => {
    const savedProducts = localStorage.getItem('farmProducts');
    const products = savedProducts ? JSON.parse(savedProducts) : [];
    const updatedProducts = products.filter(p => p.id !== productId);
    
    localStorage.setItem('farmProducts', JSON.stringify(updatedProducts));
    window.dispatchEvent(new CustomEvent('productsUpdated', {
      detail: { products: updatedProducts, count: updatedProducts.length }
    }));
    
    console.log('❌ Produit supprimé');
  };

  // Gestionnaire de catégories
  const CategoryManager = () => (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      zIndex: 10001,
      minWidth: '400px'
    }}>
      <h3>Gestion des Catégories</h3>
      <div style={{ marginBottom: '20px' }}>
        {categories.map((category, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <input
              type="text"
              value={category}
              onChange={(e) => {
                const newCategories = [...categories];
                newCategories[index] = e.target.value;
                saveCategories(newCategories);
              }}
              style={{ flex: 1, marginRight: '10px', padding: '5px' }}
            />
            <button
              onClick={() => {
                const newCategories = categories.filter((_, i) => i !== index);
                saveCategories(newCategories);
              }}
              style={{ background: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px' }}
            >
              Supprimer
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={() => {
          const newCategories = [...categories, 'Nouvelle Catégorie'];
          saveCategories(newCategories);
        }}
        style={{ background: '#28a745', color: 'white', border: 'none', padding: '10px', borderRadius: '5px', marginRight: '10px' }}
      >
        Ajouter Catégorie
      </button>
      <button
        onClick={() => setShowCategoryManager(false)}
        style={{ background: '#6c757d', color: 'white', border: 'none', padding: '10px', borderRadius: '5px' }}
      >
        Fermer
      </button>
    </div>
  );

  // Gestionnaire d'unités
  const UnitManager = () => (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      zIndex: 10001,
      minWidth: '400px'
    }}>
      <h3>Gestion des Unités</h3>
      <div style={{ marginBottom: '20px' }}>
        {units.map((unit, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <input
              type="text"
              value={unit}
              onChange={(e) => {
                const newUnits = [...units];
                newUnits[index] = e.target.value;
                saveUnits(newUnits);
              }}
              style={{ flex: 1, marginRight: '10px', padding: '5px' }}
            />
            <button
              onClick={() => {
                const newUnits = units.filter((_, i) => i !== index);
                saveUnits(newUnits);
              }}
              style={{ background: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px' }}
            >
              Supprimer
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={() => {
          const newUnits = [...units, 'Nouvelle Unité'];
          saveUnits(newUnits);
        }}
        style={{ background: '#28a745', color: 'white', border: 'none', padding: '10px', borderRadius: '5px', marginRight: '10px' }}
      >
        Ajouter Unité
      </button>
      <button
        onClick={() => setShowUnitManager(false)}
        style={{ background: '#6c757d', color: 'white', border: 'none', padding: '10px', borderRadius: '5px' }}
      >
        Fermer
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10000
      }} onClick={onClose} />

      {/* Interface principale */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        padding: '30px',
        borderRadius: '15px',
        boxShadow: '0 15px 40px rgba(0,0,0,0.3)',
        zIndex: 10001,
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#dc3545' }}>
            {editingProduct ? 'Modifier le Produit' : 'Ajouter un Produit'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Colonne gauche */}
            <div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Nom du produit 
                </label>
                <input
                  type="text"
                  name="name"
                  value={productForm.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Prix (€)
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    name="price"
                    value={productForm.price}
                    onChange={handleInputChange}
                    step="0.01"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 35px 12px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#666',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    €
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Stock 
                </label>
                <input
                  type="number"
                  name="stock"
                  value={productForm.stock}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={productForm.description}
                  onChange={handleInputChange}
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  <input
                    type="checkbox"
                    name="featured"
                    checked={productForm.featured}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Produit coup de cœur</span>
                </label>
              </div>
            </div>

            {/* Colonne droite */}
            <div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Catégorie 
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    name="category"
                    value={productForm.category}
                    onChange={handleInputChange}
                    required
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowCategoryManager(true)}
                    style={{
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ⚙️
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Unité
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    name="unit"
                    value={productForm.unit}
                    onChange={handleInputChange}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    {units.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowUnitManager(true)}
                    style={{
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ⚙️
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Image du produit
                </label>
                
                {/* Bouton pour sélectionner un fichier */}
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    style={{
                      display: 'inline-block',
                      padding: '10px 15px',
                      background: '#007bff',
                      color: 'white',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    📁 Choisir une image
                  </label>
                </div>

                {/* Champ URL (optionnel) */}
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="url"
                    name="image"
                    value={productForm.image}
                    onChange={handleInputChange}
                    placeholder="Ou saisir une URL d'image..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Aperçu de l'image */}
                {imagePreview && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '5px', 
                      fontWeight: 'bold',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      Aperçu :
                    </label>
                    <img
                      src={imagePreview}
                      alt="Aperçu du produit"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '150px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📝 {editingProduct ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>

        {/* Liste des produits existants */}
        {products.length > 0 && (
          <div style={{ marginTop: '30px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
            <h3>Produits existants ({products.length})</h3>
            <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '8px' }}>
              {products.map(product => (
                <div key={product.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '15px',
                  borderBottom: '1px solid #eee',
                  background: '#f9f9f9',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e9ecef'}
                onMouseLeave={(e) => e.target.style.background = '#f9f9f9'}
                >
                  {/* Image du produit */}
                  <div style={{ marginRight: '15px', flexShrink: 0 }}>
                    <img
                      src={product.image || '/images/beef-steak.svg'}
                      alt={product.name}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #ddd'
                      }}
                      onError={(e) => {
                        e.target.src = '/images/beef-steak.svg';
                      }}
                    />
                  </div>

                  {/* Informations du produit */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <h4 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                        {product.name}
                      </h4>
                      {product.featured && (
                        <span style={{
                          background: '#ffc107',
                          color: '#333',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          ⭐ VEDETTE
                        </span>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                      <span><strong>Catégorie:</strong> {product.category}</span>
                      <span><strong>Prix:</strong> {product.price}€/{product.unit}</span>
                      <span><strong>Stock:</strong> {product.stock} {product.unit}</span>
                    </div>
                    
                    {product.description && (
                      <div style={{ fontSize: '12px', color: '#888', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {product.description}
                      </div>
                    )}
                  </div>

                  {/* Boutons d'action */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={() => editProduct(product)}
                      style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => deleteProduct(product.id)}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modales de gestion */}
      {showCategoryManager && <CategoryManager />}
      {showUnitManager && <UnitManager />}
    </>
  );
};

export default ProductManagementInterface;
